<template>
  <tbody class="sp-table-body" :class="classModifiers" part="body">
    <tr v-if="isEmpty" class="sp-table-body__empty-row">
      <td class="sp-table-body__empty" part="empty" :colspan="columnCount">
        <slot name="empty"></slot>
      </td>
    </tr>

    <tr
      v-for="(item, index) in items"
      :key="rowKeyFor(item, index, rowKey)"
      part="row"
      class="sp-table-body__row"
      v-bind="attrsFor(item, index)"
      @click="onRowClick($event, item, index)"
    >
      <td
        v-for="column in columns"
        :key="column.field"
        part="cell"
        class="sp-table-body__cell"
        :class="cellClass(column)"
        :style="cellStyle(column)"
      >
        <TableCell :column="column" :item="item" />
      </td>
    </tr>
  </tbody>
</template>

<script setup>
import { computed } from "vue";

import { cellClass, cellStyle, rowKeyFor, TableCell } from "#ce/components/SpTable/columns.js";

const INTERACTIVE_SELECTOR =
  "a[href], button, input, select, textarea, label, summary, [role=button], [role=link], " +
  // contenteditable is enumerated: the bare and empty forms enable editing, only "false" disables it.
  '[contenteditable]:not([contenteditable="false"])';

const props = defineProps({
  columns: {
    type: Array,
    default: () => [],
  },
  /**
   * The rows to render, already sorted and sliced.
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Field whose value identifies a row.
   */
  rowKey: {
    type: String,
    default: undefined,
  },
  /**
   * Attributes merged onto each row, as `(row, index) => Object`.
   */
  rowAttrs: {
    type: Function,
    default: undefined,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  striped: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["row-click"]);

const classModifiers = computed(() => ({
  "--hover": props.hover,
  "--striped": props.striped,
  "--loading": props.loading,
}));

const isEmpty = computed(() => props.items.length === 0);
const columnCount = computed(() => props.columns.length || 1);

function attrsFor(item, index) {
  return props.rowAttrs ? props.rowAttrs(item, index) : undefined;
}

function onRowClick(event, item, index) {
  if (hasInteractiveTarget(event)) {
    return;
  }

  emit("row-click", { item, index });
}

// A control in a cell acts on its own row, so a click that reaches one is not a click on the row.
// The composed path crosses shadow boundaries, so a control an sp-* element renders is found here
// through the native element inside it.
function hasInteractiveTarget(event) {
  for (const node of event.composedPath()) {
    if (node === event.currentTarget) {
      return false;
    }

    if (node instanceof Element && node.matches(INTERACTIVE_SELECTOR)) {
      return true;
    }
  }

  return false;
}
</script>

<style lang="scss" scoped>
@use "../shared/cells";

.sp-table-body {
  &__cell {
    @include cells.cell-geometry;
    @include cells.cell-alignment;
  }

  &__empty {
    padding: var(--sp-comp-table-empty-padding, var(--sp-ref-scale-16));
    text-align: center;
    color: var(--table-header-color);
  }

  &__row:last-child &__cell {
    border-bottom: none;
  }

  &.--striped &__row:nth-child(even) {
    background-color: var(--sp-comp-table-striped-background-color, var(--sp-sys-color-surface-variant));
  }

  &.--hover &__row {
    cursor: pointer;

    &:hover {
      background-color: var(--sp-comp-table-hover-background-color, var(--sp-sys-color-surface-variant));
    }
  }

  &.--loading {
    opacity: var(--sp-comp-table-loading-opacity, 0.5);
  }
}
</style>
